/**
 * Survey helper — "Ask Users" feature.
 *
 * Super Admin builds a survey (surveys/{id}); the company OWNER is shown an
 * unskippable popup on login until they submit. One response per company at
 * surveys/{id}/responses/{companyId}. Dropdown fields can be mapped to a company
 * field (e.g. heardFrom) so the answer back-fills older accounts, and those
 * companies are only asked if that field is still blank.
 */

import { db } from '../config/firebase';
import {
  collection, doc, getDoc, getDocs, query, where, orderBy, setDoc, updateDoc,
} from 'firebase/firestore';

export const FIELD_TYPES = [
  { value: 'dropdown', label: 'Dropdown (single pick)' },
  { value: 'short', label: 'Short text' },
  { value: 'long', label: 'Long text' },
];

// Non-random unique-ish id for a new field (Date/Math.random are fine here — this
// runs in the browser, not a workflow).
export function newFieldId() {
  return 'f_' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36);
}

function audienceMatches(survey, company) {
  const plan = company?.subscription?.plan;
  if (survey.audience === 'trial') return !company?.subscription || plan === 'trial';
  if (survey.audience === 'paid') return !!plan && plan !== 'trial'; // any paid cycle (monthly/quarterly/half-yearly/yearly)
  return true; // 'all'
}

// A field is "already satisfied" for this company when it maps to a company field
// that is already filled — so we don't re-ask people who have that data.
function fieldApplies(field, company) {
  if (field.mapsTo) {
    const existing = (company[field.mapsTo] ?? '').toString().trim();
    if (existing) return false;
  }
  return true;
}

/**
 * Returns the survey the given owner must answer (with only the fields that still
 * apply to their company), or null. Only the company owner is ever gated.
 */
export async function loadPendingSurvey(company, userId) {
  if (!company?.id || !userId) return null;
  // Only the owner answers (one response per company).
  if (company.ownerId && userId !== company.ownerId) return null;

  const snap = await getDocs(query(collection(db, 'surveys'), where('active', '==', true)));
  const now = new Date().toISOString();
  const surveys = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => !s.endDate || s.endDate >= now)
    .filter((s) => audienceMatches(s, company))
    // Newest first so, if more than one is active, we show the latest.
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

  for (const s of surveys) {
    const applicableFields = (s.fields || []).filter((f) => fieldApplies(f, company));
    if (applicableFields.length === 0) continue; // nothing left to ask this company
    const rDoc = await getDoc(doc(db, 'surveys', s.id, 'responses', company.id));
    if (rDoc.exists()) continue; // already answered
    return { ...s, fields: applicableFields };
  }
  return null;
}

/**
 * Save the company's response and back-fill any mapped company fields (heardFrom).
 * @param {object} survey  the survey returned by loadPendingSurvey
 * @param {object} company activeCompany
 * @param {object} user    { uid, email }
 * @param {object} answers { [fieldId]: value }
 */
export async function submitSurvey(survey, company, user, answers) {
  await setDoc(doc(db, 'surveys', survey.id, 'responses', company.id), {
    companyId: company.id,
    companyName: company.companyName || '',
    userId: user.uid,
    userEmail: user.email || '',
    answers: answers || {},
    submittedAt: new Date().toISOString(),
  });

  const companyUpdates = {};
  (survey.fields || []).forEach((f) => {
    if (f.mapsTo && answers[f.id]) companyUpdates[f.mapsTo] = answers[f.id];
  });
  if (Object.keys(companyUpdates).length) {
    try { await updateDoc(doc(db, 'companies', company.id), companyUpdates); }
    catch (e) { console.warn('survey mapsTo update failed (non-fatal):', e.message); }
  }
}

// ── Super Admin side ──
export async function loadSurveys() {
  const snap = await getDocs(query(collection(db, 'surveys'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadResponses(surveyId) {
  const snap = await getDocs(collection(db, 'surveys', surveyId, 'responses'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export default { loadPendingSurvey, submitSurvey, loadSurveys, loadResponses, newFieldId, FIELD_TYPES };
